/* eslint-disable max-classes-per-file */
import React from 'react';

import cn from 'classnames';
import uuid from 'react-uuid';
import PropTypes from 'prop-types';
import uploadcare from 'uploadcare-widget';
import { HiPlus, HiMinus } from 'react-icons/hi';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from 'ckeditor5-custom-build/build/ckeditor';
// import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

class Uploader {
  constructor(loader) {
    this.loader = loader;
  }

  // Starts the upload process.
  upload = () =>
    // eslint-disable-next-line no-async-promise-executor
    new Promise(async (resolve, reject) => {
      try {
        const file = await this.loader.file;
        const dialog = uploadcare.openDialog(
          [
            uploadcare.fileFrom('object', file, {
              publicKey:
                process.env.REACT_APP_UPLOADCARE_KEY || 'demopublickey',
            }),
          ],
          {
            publicKey: process.env.REACT_APP_UPLOADCARE_KEY || 'demopublickey',
            imageOnly: true,
            tabs: 'file url',
            effects: 'crop,rotate,mirror',
            crop: '',
            previewStep: true,
          }
        );

        dialog.fail(() => {
          reject(new Error('Uploading image is canceled'));
        });

        dialog.done((upload) => {
          upload.done((res) => {
            resolve({
              default: res.cdnUrl,
            });
          });
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error, ' here');
      }
    });
}

class ImageUploaderAdapter {
  constructor(editor) {
    // eslint-disable-next-line no-param-reassign
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) =>
      new Uploader(loader);
  }
}

const CONFIG = {
  toolbar: [
    'heading',
    '|',
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'link',
    'bulletedList',
    'numberedList',
    '|',
    'alignment',
    'fontSize',
    // "|",
    // "imageInsert",
    // "mediaEmbed",
    '|',
    'insertTable',
    'blockQuote',
    '|',
    'undo',
    'redo',
  ],
  extraPlugins: [ImageUploaderAdapter],
};

const FormRichText = (props) => {
  const {
    type,
    name,
    label,
    value,
    icon,
    error,
    prefix,
    onChange,
    required,
    className,
    iconAlign,
    labelClassName,
    onSetFieldValue,
    withShowPassword,
    containerClassName,
    disabled,
    ...rest
  } = props;
  const [id] = React.useState(uuid());
  const [showExpand, setShowExpand] = React.useState(false);
  React.useEffect(() => {
    setTimeout(() => {
      const el = document.getElementById(id);
      setShowExpand(el?.clientHeight > 200);
    }, 200);
  }, [id, setShowExpand]);
  const [showAll, setShowAll] = React.useState(!value);
  const handleOnFocus = (e) => {
    e.preventDefault();
    try {
      document.getElementById(rest?.id ?? name).focus();
    } catch (err) {
      // do_nothing
    }
  };

  const handleOnChange = (e, editor) => {
    const data = editor.getData();
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, data);
      return;
    }
    if (typeof onChange === 'function')
      onChange((prev) => ({
        ...prev,
        [name]: data,
      }));
  };

  const handleToggle = (e) => {
    e.preventDefault();
    setShowAll(!showAll);
  };

  return (
    <div>
      <div className="w-full relative">
        <div
          className={cn(
            'form-container transition duration-300 ease-in-out',
            containerClassName,
            {
              'form-container': !error,
              'form-container-error': error,
            }
          )}
          onClick={handleOnFocus}
          role="presentation"
        >
          {label && (
            <label
              className={cn('form-label', labelClassName)}
              htmlFor={rest?.id ?? name}
            >
              {label} {required ? <span className="text-red-500">*</span> : ''}
            </label>
          )}
          <div className="relative">
            {prefix && (
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <span
                  className={cn(
                    'transition duration-300 ease-in-out text-gray-400 font-light',
                    {
                      'form-input-prefix-error': error,
                      'form-input-prefix': !error,
                    }
                  )}
                >
                  {prefix}
                </span>
              </div>
            )}
            <div
              key={showAll ? '1' : '0'}
              className={cn('prose max-w-full', {
                'overflow-hidden': !showAll && showExpand,
              })}
              style={{ maxHeight: !showAll && showExpand ? '200px' : 'auto' }}
            >
              <div id={id}>
                <CKEditor
                  data={value}
                  // onInit={(editor) => {}}
                  config={CONFIG}
                  editor={ClassicEditor}
                  onChange={handleOnChange}
                  disabled={disabled}
                />
              </div>
            </div>
            {showExpand ? (
              <div className="text-center relative">
                {!showAll ? (
                  <div className="absolute left-0 -top-20 w-full h-20 bg-gradient-to-b from-transparent to-white z-10" />
                ) : null}
                <a
                  className="text-primary-500 text-xs flex items-center justify-center hover:underline"
                  href="/"
                  onClick={handleToggle}
                >
                  {showAll ? (
                    <>
                      <HiMinus /> Minimize
                    </>
                  ) : (
                    <>
                      <HiPlus /> Expand
                    </>
                  )}
                </a>
              </div>
            ) : null}
          </div>
        </div>
        {error && <small className=" form-error-badge">{error ?? ''}</small>}
      </div>
    </div>
  );
};

FormRichText.defaultProps = {
  label: 'Name',
  containerClassName: '',
  labelClassName: '',
  value: '',
  className: '',
  required: false,
  type: 'text',
  error: false,
  icon: false,
  withShowPassword: false,
  onSetFieldValue: false,
  onChange: false,
  prefix: false,
  disabled: false,
  iconAlign: 'right-2',
};

FormRichText.propTypes = {
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.bool,
  ]),
  withShowPassword: PropTypes.bool,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  iconAlign: PropTypes.string,
  containerClassName: PropTypes.string,
  labelClassName: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.element, PropTypes.bool]),
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
  onSetFieldValue: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Object),
  ]),
  prefix: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.instanceOf(Object),
  ]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.instanceOf(Object)]),
};

export default React.memo(FormRichText);
